import React from 'react';

import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';

import { DiscountAppliesTo, discountAppliesToOptions } from '../../models';
import { AppliedFilters } from '../shared/applied-filters';
import { AvailableToWithOverflow } from '../shared/available-to-with-overflow';
import { Condition } from '../shared/condition';
import { FilteredProductCount } from '../shared/filtered-product-count';
import {
  getDayOfTheWeekText,
  getEndDateText,
  getStartDateText,
  getTimeRangeText,
  isAnyDayOfWeekActive,
} from '../shared/helpers';
import { SummaryFieldTitle, SummaryFieldValue } from '../shared/styled';
import { useSelectedDetailsResources } from '../shared/use-selected-details-resources';
import { useDiscountWizardStateContext } from '../state';
import { applicationMethodNames } from '../steps/configure/use-application-methods';
import { ApplicationMethodTypeId } from '../types/index';

import { ExternalId } from './external-id';
import { Reward } from './reward';
import { SummarySidebarSection, SummarySidebarHeading, AppliedFiltersWrapper, SummarySidebarContainer } from './styled';

export function SummarySidebar() {
  const isOrderSourcesEnabled = useLDFlag(LD.DISCOUNT_BY_ORDER_TYPE_MENU_TYPE, false);
  const {
    configureStepState: { applicationMethod, discountCode, discountName, loyaltyPointsToRedeem },
    detailsStepState: {
      activeDaysOfTheWeek,
      availableOnline,
      endDate,
      endTime,
      restrictByDayOfWeek,
      specifyEndDate,
      startDate,
      startTime,
      visited: detailsStepVisited,
    },
    isManualDiscount,
    requirementsStepState: { activeFilter: _, visited: requirementsStepVisited, ...requirements },
    rewardsStepState: { discountAppliesTo, visited: rewardsStepVisited, allowStacking },
    onlineDetailsStepState: { onlineName, rank, description },
    discountId,
  } = useDiscountWizardStateContext();

  const {
    selectedCustomerTypes,
    selectedCustomerGroups,
    selectedLocations,
    selectedCustomerSegments,
    selectedOrderSources,
  } = useSelectedDetailsResources();

  const discountAppliesToText = discountAppliesToOptions.find((option) => option.val === discountAppliesTo)?.name || '';

  // conditional rendering conditions
  const isExistingDiscount = discountId !== 0;
  const showCode = discountCode !== '';
  const showLoyaltyPointsToRedeem =
    applicationMethod === ApplicationMethodTypeId.DutchieLoyalty ||
    applicationMethod === ApplicationMethodTypeId.ExternalLoyalty ||
    (applicationMethod === ApplicationMethodTypeId.Manually && !!loyaltyPointsToRedeem);
  const showDetailsSection = isExistingDiscount || detailsStepVisited;
  const showRecurrenceSection = isExistingDiscount || restrictByDayOfWeek;
  const showRecurrenceTimeRange = isExistingDiscount || isAnyDayOfWeekActive(activeDaysOfTheWeek);
  const showRequirementsSection = isExistingDiscount || requirementsStepVisited;
  const showRewardSection = isExistingDiscount || rewardsStepVisited;
  const showRewardProductsSection = discountAppliesTo === DiscountAppliesTo.DifferentProducts;
  const showOnlineDetailsSection = isExistingDiscount;

  return (
    <SummarySidebarContainer>
      <SummarySidebarHeading>Summary</SummarySidebarHeading>
      <SummarySidebarSection>
        <SummaryFieldTitle>Discount name:</SummaryFieldTitle>
        <SummaryFieldValue>{discountName || '---'}</SummaryFieldValue>
        {showCode && (
          <>
            <SummaryFieldTitle>Code:</SummaryFieldTitle>
            <SummaryFieldValue>{discountCode}</SummaryFieldValue>
          </>
        )}
        <SummaryFieldTitle>Application method:</SummaryFieldTitle>
        <SummaryFieldValue>{applicationMethodNames[applicationMethod]}</SummaryFieldValue>
        {showLoyaltyPointsToRedeem && (
          <>
            <SummaryFieldTitle>Loyalty points to redeem:</SummaryFieldTitle>
            <SummaryFieldValue>{loyaltyPointsToRedeem}</SummaryFieldValue>
          </>
        )}
        <ExternalId />
      </SummarySidebarSection>
      {showDetailsSection && (
        <SummarySidebarSection>
          <SummaryFieldTitle>Available to:</SummaryFieldTitle>
          <AvailableToWithOverflow noun='customer types' options={selectedCustomerTypes} />
          <SummaryFieldTitle />
          <AvailableToWithOverflow noun='customer groups' options={selectedCustomerGroups} />
          <SummaryFieldTitle />
          <AvailableToWithOverflow noun='customer segments' options={selectedCustomerSegments} />
          <SummaryFieldTitle>Schedule:</SummaryFieldTitle>
          <SummaryFieldValue>Start: {getStartDateText(startDate)}</SummaryFieldValue>
          <SummaryFieldTitle />
          <SummaryFieldValue>End: {getEndDateText(endDate, specifyEndDate)}</SummaryFieldValue>
          {showRecurrenceSection && (
            <>
              <SummaryFieldTitle>Recurrence</SummaryFieldTitle>
              <SummaryFieldValue>{getDayOfTheWeekText(activeDaysOfTheWeek)}</SummaryFieldValue>
              {showRecurrenceTimeRange && (
                <>
                  <SummaryFieldTitle />
                  <SummaryFieldValue>{getTimeRangeText(startTime, endTime)}</SummaryFieldValue>
                </>
              )}
            </>
          )}
          <SummaryFieldTitle>Available online:</SummaryFieldTitle>
          <SummaryFieldValue>{availableOnline ? 'Yes' : 'No'}</SummaryFieldValue>
          {isOrderSourcesEnabled && availableOnline && (
            <>
              <SummaryFieldTitle>Order source(s):</SummaryFieldTitle>
              <AvailableToWithOverflow noun='order sources' options={selectedOrderSources} />
            </>
          )}
          <SummaryFieldTitle>Available locations:</SummaryFieldTitle>
          <AvailableToWithOverflow noun='locations' options={selectedLocations} />
        </SummarySidebarSection>
      )}
      {showRequirementsSection &&
        Object.keys(requirements).map((requirement) => (
          <SummarySidebarSection key={`requirement-${requirement}`}>
            <SummaryFieldTitle>Requirement {requirement}</SummaryFieldTitle>
            <SummaryFieldValue />
            {!isManualDiscount && (
              <>
                <SummaryFieldTitle>Condition:</SummaryFieldTitle>
                <SummaryFieldValue>
                  <Condition requirement={Number(requirement)} />
                </SummaryFieldValue>
              </>
            )}
            <SummaryFieldTitle>Filters applied:</SummaryFieldTitle>
            <AppliedFiltersWrapper>
              <AppliedFilters excludeCommas requirement={Number(requirement)} />
            </AppliedFiltersWrapper>
            <SummaryFieldTitle>Eligible products:</SummaryFieldTitle>
            <SummaryFieldValue>
              <FilteredProductCount requirement={Number(requirement)} />
            </SummaryFieldValue>
          </SummarySidebarSection>
        ))}
      {showRewardSection && (
        <SummarySidebarSection>
          <Reward />
          <SummaryFieldTitle>Applies to:</SummaryFieldTitle>
          <SummaryFieldValue>{discountAppliesToText}</SummaryFieldValue>
          <SummaryFieldTitle>Allow stacking:</SummaryFieldTitle>
          <SummaryFieldValue>{allowStacking ? 'Yes' : 'No'}</SummaryFieldValue>

          {showRewardProductsSection && (
            <>
              <SummaryFieldTitle>Reward products</SummaryFieldTitle>
              <SummaryFieldValue />
              {!isManualDiscount && (
                <>
                  <SummaryFieldTitle>Condition:</SummaryFieldTitle>
                  <SummaryFieldValue>
                    <Condition requirement='reward' />
                  </SummaryFieldValue>
                </>
              )}
              <SummaryFieldTitle>Filters applied:</SummaryFieldTitle>
              <AppliedFiltersWrapper>
                <AppliedFilters excludeCommas requirement='reward' />
              </AppliedFiltersWrapper>
              <SummaryFieldTitle>Eligible products:</SummaryFieldTitle>
              <SummaryFieldValue>
                <FilteredProductCount requirement='reward' />
              </SummaryFieldValue>
            </>
          )}
        </SummarySidebarSection>
      )}

      {showOnlineDetailsSection && (
        <SummarySidebarSection>
          <SummaryFieldTitle>Online name:</SummaryFieldTitle>
          <SummaryFieldValue>{onlineName}</SummaryFieldValue>
          {description && (
            <>
              <SummaryFieldTitle>Description:</SummaryFieldTitle>
              <SummaryFieldValue>{description}</SummaryFieldValue>
            </>
          )}
          <SummaryFieldTitle>Rank:</SummaryFieldTitle>
          <SummaryFieldValue>{rank}</SummaryFieldValue>
        </SummarySidebarSection>
      )}
    </SummarySidebarContainer>
  );
}
